import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { ImageData, Location, Space, useStandardizeImages } from "../data";
import { useGetPartnerOffering } from "../data/api";
import { Testimonial } from "../data/partners";
import { buildPathWithParams, useQueryString } from "../hooks";
import { FirstDayOfWeek } from "../lib/datetime";
import { uniqueBy } from "../lib/js_utils/array_utils";
import { useTrackProductViewedEcommerceEventOnMount } from "./analytics_ecommerce_events";
import {
  useLocationQuery,
  usePartnerCatalogQueryMaybe,
  useQuoteQuery,
  useSpaceQuery,
  useSpacesQuery,
} from "./api";
import { DayPassWithCapacityCheckoutBoxDesktop } from "./checkout_box_desktop";
import { DayPassWithCapacityCheckoutBoxMobile } from "./checkout_box_mobile";
import { useCurrentQueryString, useSyncQueryString } from "./offering_helpers";
import {
  normalizeDayPassWithCapacityOrderParams,
  toQuoteInput,
  useSpaceAvailableDaysQuery,
  useDayPassWithCapacityOrderParamsHandlers,
} from "./order_params";
import { PartnerConfig } from "./partner_config";
import {
  GeneralInformation,
  OfferingDetailsBase,
} from "./template_offering_details_base";
import { FAQ } from "./template_offering_details_common";

interface OfferingDetailsDayPassWithCapacityParams {
  offeringID: string;
}

export interface OfferingDetailsDayPassWithCapacityQueryParams {
  spaceID?: string;
  partnerOfferingLocationID?: string;
  startDate?: string;
  endDate?: string;
  capacity?: string;
  fromCatalog?: string;
  promoCode?: string;
}

interface OfferingDetailsDayPassWithCapacityProps {
  firstDayOfWeek?: FirstDayOfWeek;
  showTalkWithUsPrompt?: boolean;
  showBackButton?: boolean;
  showAddress?: boolean;
  showDirections: boolean;
  capacityOptionLabel?: string;
  getDesktopImagesBasedOnLocation?: (location?: Location) => ImageData[];
  getMobileImagesBasedOnLocation?: (location?: Location) => ImageData[];
  partnerConfig: PartnerConfig;
  useLegacyImages?: boolean;
  data?: {
    title?: string | React.ReactNode;
    description?: React.ReactNode;
    amenitiesDescription?: string;
    faqs?: FAQ[];
    testimonials?: Testimonial[];
    generalInfo?: GeneralInformation;
  };
}

export function OfferingDetailsDayPassWithCapacity(
  props: OfferingDetailsDayPassWithCapacityProps
) {
  const {
    partnerConfig,
    data,
    firstDayOfWeek,
    showTalkWithUsPrompt,
    showBackButton = false,
    showAddress = false,
    useLegacyImages = false,
    showDirections,
    capacityOptionLabel,
    getDesktopImagesBasedOnLocation,
    getMobileImagesBasedOnLocation,
  } = props;
  const pageParams = useParams<OfferingDetailsDayPassWithCapacityParams>();
  const queryParams =
    useQueryString<OfferingDetailsDayPassWithCapacityQueryParams>();
  const pageParamsOfferingID = pageParams.offeringID;
  const offeringID = pageParamsOfferingID;
  const partnerOffering = useGetPartnerOffering(offeringID);
  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryParams.fromCatalog
  );

  const uniquePartnerOfferingLocations = useMemo(() => {
    return uniqueBy(partnerOffering.offeringLocations, (l) => l.id);
  }, [partnerOffering.offeringLocations]);
  const firstPartnerOfferingLocation = uniquePartnerOfferingLocations[0];
  const defaultSpaceID = firstPartnerOfferingLocation.spaceID;

  const history = useHistory();
  const {
    orderParams,
    orderParamsError,
    handleChangeCapacity,
    handleChangeDayPassDateRange,
    handleChangeLocation,
    handleChangeSpace,
    handleChangePromoCode,
  } = useDayPassWithCapacityOrderParamsHandlers({
    ...normalizeDayPassWithCapacityOrderParams({
      ...queryParams,
      promoCode: queryParams.promoCode,
      partnerOfferingLocationID:
        uniquePartnerOfferingLocations.length > 1
          ? queryParams.partnerOfferingLocationID
          : firstPartnerOfferingLocation.id,
    }),
  });

  useEffect(() => {
    if (partnerCatalog?.promoCode && !queryParams.promoCode) {
      handleChangePromoCode(partnerCatalog?.promoCode);
    }
  }, [handleChangePromoCode, partnerCatalog, queryParams.promoCode]);

  const allSpaceIDs = useMemo(() => {
    return partnerOffering.offeringLocations.map((l) => l.spaceID);
  }, [partnerOffering.offeringLocations]);
  const allSpaces = useSpacesQuery(allSpaceIDs);
  const spaces = useMemo(() => {
    if (!orderParams.partnerOfferingLocationID) {
      return [];
    }

    const filtered = partnerOffering.offeringLocations.filter(
      (l) => l.id === orderParams.partnerOfferingLocationID
    );
    const result: Space[] = [];

    for (const partnerOfferingLocation of filtered) {
      const space = allSpaces.find(
        (s) => s.id === partnerOfferingLocation.spaceID
      );
      if (space) {
        result.push(space);
      }
    }

    return result;
  }, [
    partnerOffering.offeringLocations,
    orderParams.partnerOfferingLocationID,
    allSpaces,
  ]);
  const selectedSpaceID = useMemo(() => {
    const derivedSpace = spaces.find((s) => {
      return s.layouts.find((l) => l.capacity === orderParams.capacity);
    });

    return derivedSpace?.id;
  }, [spaces, orderParams.capacity]);

  const defaultSpaceQuery = useSpaceQuery(defaultSpaceID);
  const { data: defaultSpace, loading } = defaultSpaceQuery;

  const selectedSpaceQuery = useSpaceQuery(selectedSpaceID);
  const { data: space } = selectedSpaceQuery;
  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      selectedSpaceID && spaceLayoutID
        ? toQuoteInput(
            selectedSpaceID,
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: spaceLayoutID,
              attendees: 1,
            }
          )
        : null,
    [selectedSpaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );
  const { isOutsideRange, isBlockedDate, handleNextVisibleDates } =
    useSpaceAvailableDaysQuery(selectedSpaceID);
  const partnerOfferingLocation = useMemo(() => {
    return uniquePartnerOfferingLocations.find(
      (l) => l.id === orderParams.partnerOfferingLocationID
    );
  }, [orderParams.partnerOfferingLocationID, uniquePartnerOfferingLocations]);
  const selectedLocationQuery = useLocationQuery(
    partnerOfferingLocation?.location.id
  );
  const { data: location } = selectedLocationQuery;

  const {
    data: quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuoteQuery(quoteInput);
  const capacities = useMemo(() => {
    const caps = [];

    for (const space of spaces) {
      for (const layout of space.layouts) {
        caps.push(layout.capacity);
      }
    }
    caps.sort((a, b) => a - b);
    return caps;
  }, [spaces]);

  const queryString = useCurrentQueryString(orderParams);

  useSyncQueryString(orderParams);
  const offeringName =
    defaultSpace && location
      ? `${defaultSpace.productType?.name} to ${location.name}`
      : "";
  useTrackProductViewedEcommerceEventOnMount(offeringName, space, location);
  useEffect(() => {
    if (selectedSpaceID) {
      handleChangeSpace(selectedSpaceID);
    }
  }, [selectedSpaceID, handleChangeSpace]);

  const handleContinue = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/checkout/${pageParamsOfferingID}?${queryString}`
    );
    history.push(pathWithParams);
  }, [history, pageParamsOfferingID, queryString]);

  const handlePressBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      const pathWithParams = buildPathWithParams(`/offerings/${offeringID}`, {
        reset: true,
        keepOnReset: ["fromCatalog"],
      });
      history.replace(pathWithParams);
    }
  }, [history, offeringID]);

  useEffect(() => {
    if (queryParams.spaceID && !loading && !defaultSpace) {
      const pathWithParams = buildPathWithParams(`/offerings/${offeringID}`);
      history.replace(pathWithParams);
    }
  }, [defaultSpace, queryParams.spaceID, offeringID, history, loading]);

  const title = partnerOffering.offeringTitle || data?.title;
  const images = useStandardizeImages(
    partnerOffering,
    location,
    space,
    selectedSpaceID,
    useLegacyImages,
    {
      imagesDesktop: getDesktopImagesBasedOnLocation
        ? getDesktopImagesBasedOnLocation(location)
        : [],
      imagesMobile: getMobileImagesBasedOnLocation
        ? getMobileImagesBasedOnLocation(location)
        : [],
      imagesModal: getDesktopImagesBasedOnLocation
        ? getDesktopImagesBasedOnLocation(location)
        : [],
    }
  );

  return (
    <OfferingDetailsBase
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      showAddress={showAddress}
      showDirections={showDirections}
      partnerOffering={partnerOffering}
      partnerOfferingID={offeringID}
      partnerConfig={partnerConfig}
      space={defaultSpace}
      onPressBack={showBackButton ? handlePressBack : undefined}
      data={{
        ...data,
        ...images,
        title,
        offeringName,
        location,
      }}
      checkoutBoxDesktop={
        <DayPassWithCapacityCheckoutBoxDesktop
          quoteError={quoteError}
          quoteLoading={quoteLoading}
          firstDayOfWeek={firstDayOfWeek}
          capacities={capacities}
          capacityOptionLabel={capacityOptionLabel}
          orderParams={orderParams}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeCapacity={handleChangeCapacity}
          onChangeLocation={handleChangeLocation}
          onChangeDateRange={handleChangeDayPassDateRange}
          onContinue={handleContinue}
          onNextVisibleDates={handleNextVisibleDates}
          locations={uniquePartnerOfferingLocations}
          location={location}
          quote={quote}
          space={space}
          datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
        />
      }
      checkoutBoxMobile={
        <DayPassWithCapacityCheckoutBoxMobile
          quoteError={quoteError}
          orderParamsError={orderParamsError}
          firstDayOfWeek={firstDayOfWeek}
          orderParams={orderParams}
          selectedSpace={space}
          capacities={capacities}
          capacityOptionLabel={capacityOptionLabel}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeLocation={handleChangeLocation}
          onChangeCapacity={handleChangeCapacity}
          onChangeDateRange={handleChangeDayPassDateRange}
          onContinue={handleContinue}
          onNextVisibleDates={handleNextVisibleDates}
          locations={uniquePartnerOfferingLocations}
          quote={quote}
          datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
        />
      }
    />
  );
}
